import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import { Bleed, Box, Card, Divider, Heading, Image, PageGrid, Stack, TextLink } from '@eneco/sparky';
import { useContent, useLayoutData } from '@sitecore/common';
import { ProspectCtaRendering } from '@sitecore/types/ProspectCta';

import { ChampionDisabledAction } from '../ChampionDisabledAction';
import { useAccountLinkDialog } from '../../providers/AccountLinkDialogProvider';

const SITECORE_NO_GRID_GUID = '2dfd92c7-5914-4d46-a1ad-7733cfdd9c70';
const SITECORE_NO_GRID_BE_GUID = '15c6d33f-232b-4b2d-b582-804e26cfe2b6';

interface CtaCardProps {
  image: { src: string; alt: string };
  title: string;
  description: string;
  link: { href?: string; target?: string; text: string };
  onClick?: () => void;
  isLink?: boolean;
  isDisabled?: boolean;
}

const CtaCard = ({ image, title, description, link, onClick, isLink, isDisabled }: CtaCardProps) => {
  const hasImage = !!image.src;

  const action = (
    <TextLink
      emphasis="high"
      href={!isDisabled && isLink ? link.href : undefined}
      target={!isDisabled && isLink ? link.target : undefined}
      onClick={!isDisabled ? onClick : undefined}>
      {link.text}
    </TextLink>
  );

  return (
    <Card>
      {hasImage && (
        <Box paddingTop={10}>
          <Image src={image.src} alt={image.alt} height="150px" width="100%" objectFit="contain" />
        </Box>
      )}
      <Box padding={6}>
        <Stack gap="1">
          <Heading as="h3" size="3XS">
            {title}
          </Heading>
          {!hasImage && <ProspectCtaDivider />}
          <RichText html={description} />
          <Box paddingTop="3">{isDisabled ? <ChampionDisabledAction>{action}</ChampionDisabledAction> : action}</Box>
        </Stack>
      </Box>
    </Card>
  );
};

const ProspectCta = () => {
  const { isProspect, isImpersonating } = useSelfServiceAccount();
  const { fields } = useContent<ProspectCtaRendering>();
  const { route } = useLayoutData();
  const [_, setIsOpen] = useAccountLinkDialog();

  if (!isProspect) return null;

  const cards = (
    <Stack direction="row" gap={6} wrap={{ initial: true, md: false }}>
      <Stack.Item>
        <CtaCard
          image={fields.prospectImage.value}
          title={fields.prospectTitle.value}
          description={fields.prospectDescription.value}
          link={fields.prospectCtaLink.value}
          isLink={true}
        />
      </Stack.Item>
      <Stack.Item>
        <CtaCard
          image={fields.customerImage.value}
          title={fields.customerTitle.value}
          description={fields.customerDescription.value}
          link={fields.customerCtaLink.value}
          onClick={() => setIsOpen(true)}
          isDisabled={isImpersonating()}
        />
      </Stack.Item>
    </Stack>
  );

  return route.layoutId === SITECORE_NO_GRID_GUID || route.layoutId === SITECORE_NO_GRID_BE_GUID ? (
    <PageGrid>
      <PageGrid.Item gridColumn={{ initial: '1/-1' }}>
        <Box paddingTop={6}>{cards}</Box>
      </PageGrid.Item>
    </PageGrid>
  ) : (
    cards
  );
};

const ProspectCtaDivider = () => {
  return (
    <Box paddingTop={3} paddingBottom={5}>
      <Bleed horizontal={6}>
        <Divider />
      </Bleed>
    </Box>
  );
};

export default ProspectCta;
